import * as Sentry from '@sentry/react';
import useSafeBarHeight from '@web/common/src/hooks/useStatusBarHeight';
import { RouteItem } from '@web/common/src/routes';
import { iOS, isWebview } from '@web/common/src/utils/env';
import { kkBridgeCallAsync, kkBridgeHasMethod } from '@web/common/src/utils/jsBridge';
import { nativeApi } from '@web/common/src/utils/nativeApi';
import { NotFound, PageErrorBoundary } from '@web/components';
import { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Route, Routes, useLocation } from 'react-router-dom';
import 'virtual:windi.css';
import { getUserInfoApi } from './appApi';
import { AppName } from './AppGlobal';
import billMgmtRoutes from './billMgmtRoutes';
import GlobalDialog from './components/GlobalDialog';
import { Spinner } from './components/Spinner';
import routes from './routes';
import './style.less';
import './styles/index.less';
import { getNacosConfig } from './utils/configUtils';
import { changeLanguage } from './utils/languageutils';

function App() {
  const location = useLocation();
  const isApp = isWebview();

  const [statusBarHeight] = useSafeBarHeight();

  const navigate = useNavigate();

  const [currentRoutes, setCurrentRoutes] = useState<RouteItem[]>([]);

  useEffect(() => {
    (async () => {
      changeLanguage(await nativeApi.getLanguage());
    })();
    (async () => {
      Sentry.setTag('appVersion', (await nativeApi.getNativeAppVersion?.()) || 'pureH5');
    })();
    window.navigateFunc = navigate;
  }, []);

  useEffect(() => {
    if (kkBridgeHasMethod('kk_naviBarHeight')) {
      let a = kkBridgeCallAsync('kk_naviBarHeight');
      const devicePixelRatio = window.devicePixelRatio || 1;

      a /= devicePixelRatio;
      const fotterHeight = `${a}px`;
      document?.documentElement?.style?.setProperty('--safe-area-inset-bottom', fotterHeight);
    } else {
      document?.documentElement?.style?.setProperty('--safe-area-inset-bottom', '0px');
    }

    document?.documentElement?.style?.setProperty('--safe-bar-height', `${statusBarHeight}px`);

    if (iOS()) {
      // iOS 中，将值设置为 env(--iOS-safe-area-inset-bottom)
      document?.documentElement?.style?.setProperty(
        '--iOS-safe-area-inset-bottom',
        'env(safe-area-inset-bottom)'
      );
      document?.documentElement?.style?.setProperty(
        '--iOS-safe-area-inset-top',
        'env(safe-area-inset-top)'
      );
    }
  }, [statusBarHeight]);

  useEffect(() => {
    (async function fn() {
      const route = routes.find((item) => item.path === location.pathname);
      if (!route) return;
      if (!isApp) {
        document.title = route.meta.name || import.meta.env.VITE_GLOB_APP_TITLE || '维信卡卡贷';
      }
    })();
  }, [location]);

  useEffect(() => {
    // 注册公共属性
    sensors.registerPage({
      platform_type: 'H5',
      $ip: '',
    });
    sensors.use('PageLeave', {
      isCollectUrl(url = window.location.href) {
        console.log('PageLeave', url);
        // url 为要采集页面浏览时长的页面地址。
        return true; // 采集
        // return false; // 不采集
      },
    });
    Promise.resolve().then(() => {
      // 尽快初始化Nacos配置
      getNacosConfig([
        'app.newversion.cashout.select.firstcoupon',
        'app.pay.dev.order.time.switch',
        'app.pay.dev.time.switch',
        'pay.paymentMinLimit',
        'app.cl.pay.cl.support.clear.card.switch',
        'app.preconfirmloan.cl.initialperiod',
        'app.newversion.h5process.allnewversion',
        'google.app.download.switch',
        'pay.payCombinationLoanCheckSwitch', // 是否开启组合贷贷款
        'product.cmc.open', // 是否开启cmc全员开放
        'credit.clOrCmc.rate.dialog',
        'user.login.h5LoginNotCheckIdCard', // 是否开启h5登录不检查身份证
        'confirm.loan.show.youtube.link', // 是否显示youtube链接
        'cl.credit.approval.success.newuser.bubbleText',
        'cl.credit.approval.success.unlock.bubbleText',
        'cl.credit.approval.success.newuser.bubbleTextEn',
        'cl.credit.approval.success.unlock.bubbleTextEn',
        'cl.credit.approval.success.newuser.bubbleTextCn',
        'cl.credit.approval.success.unlock.bubbleTextCn',
        'app.pay.interestCategory', // 利息计算方式
      ]);
      setTimeout(() => {
        getUserInfoApi({ isIntercept: false });
      }, 0); // 没必要一启动就去获取用户信息
    });
  }, []);

  useLayoutEffect(() => {
    if (window.appName === AppName.BillManagement) {
      setCurrentRoutes(billMgmtRoutes);
    } else {
      setCurrentRoutes(routes);
    }
  }, []);

  return (
    <Suspense fallback={<Spinner />}>
      <Sentry.ErrorBoundary>
        <PageErrorBoundary>
          <Routes>
            {currentRoutes.map((v) => (
              <Route key={v.path} path={v.path} element={v.component} />
            ))}
            {/* 添加pub前缀路由，用以展示无需登录的页面 */}
            <Route key="404" path="*" element={<NotFound />} />
          </Routes>
          <GlobalDialog />
        </PageErrorBoundary>
      </Sentry.ErrorBoundary>
    </Suspense>
  );
}

export default App;
